import { Pills } from '@amelio/ui-library';
import { useAtomValue } from 'jotai';

import { ActionMenu, type ActionMenuItem } from './ActionMenu';
import { ChevronDownIcon, ChevronUpIcon, UserPlusIcon } from './icons';
import { CompetencyQuestionsEditor } from './questions/CompetencyQuestionsEditor';

import type { Competency } from '@/types/competency/Competency';

import { useGetUserPermissions } from '@/api/directory';
import { userInstanceAtom } from '@/atoms/userDetails';
import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage } from '@/utils/localization';

type CompetencyCardProps = {
    competency: Competency;
    isOpen: boolean;
    onToggle: () => void;
    onAssignToEmployees: () => void;
    actions: ActionMenuItem[];
};

export const CompetencyCard = ({
    competency,
    isOpen,
    onToggle,
    onAssignToEmployees,
    actions,
}: CompetencyCardProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const isAmelioAdmin = useAtomValue(userInstanceAtom).isAmelioAdmin;
    const isCompetencyInactive = competency.status !== CompetencyStatus.ACTIVE;

    const { data: permissions } = useGetUserPermissions();
    const canAssignToEmployees =
        (permissions?.canAssignCompetencyProfiles ?? false) && !isAmelioAdmin;

    return (
        // No backdrop-blur: it would re-anchor the nested DragOverlay to this card instead of the viewport.
        <div
            className={`rounded-2xl border border-gray-200 bg-white/90 p-4 md:p-6 ${
                isCompetencyInactive ? 'opacity-60' : ''
            }`}
            data-testid={`competency-${competency.id}`}
        >
            <div className={'cmp-card-head flex items-start'}>
                <button
                    type={'button'}
                    onClick={onToggle}
                    className={'text-navy hover:text-navy mt-0.5 flex-shrink-0 transition-colors'}
                    aria-label={competency.id}
                    data-testid={`competency-toggle-${competency.id}`}
                >
                    {isOpen ? (
                        <ChevronUpIcon className={'h-6 w-6'} />
                    ) : (
                        <ChevronDownIcon className={'h-6 w-6'} />
                    )}
                </button>

                <div
                    className={'min-w-0 flex-1 cursor-pointer'}
                    role={'presentation'}
                    onClick={onToggle}
                >
                    <div className={'items-center gap-2 sm:flex'}>
                        <h3
                            className={
                                'text-xl font-medium ' +
                                (isCompetencyInactive ? 'text-gray-600 line-through' : 'text-navy')
                            }
                        >
                            {getTextByLanguage(competency.name, language)}
                        </h3>
                        {isCompetencyInactive && (
                            <span
                                className={
                                    'text-caption rounded-full bg-gray-100 px-2 py-0.5 font-normal tracking-wide text-gray-600 uppercase'
                                }
                            >
                                {t('competencies.disabled')}
                            </span>
                        )}
                    </div>

                    <p className={'cmp-card-desc text-navy text-body-sm'}>
                        {getTextByLanguage(competency.description, language)}
                    </p>

                    <div className={'cmp-card-meta flex flex-wrap items-center'}>
                        {!isAmelioAdmin && (
                            <span className={'text-caption text-gray-500'}>
                                {t('competencies.assignedSummary', {
                                    profiles: t('competencies.assignedProfiles', {
                                        count: competency.assignedProfilesCount ?? 0,
                                    }),
                                    employees: t('competencies.assignedEmployees', {
                                        count: competency.assignedEmployeesCount ?? 0,
                                    }),
                                })}
                            </span>
                        )}

                        <Pills
                            variant={'purple'}
                            size={'sm'}
                            data-testid={`competency-question-count-${competency.id}`}
                            className={'text-caption whitespace-nowrap'}
                        >
                            {t('competencies.questionCount', {
                                count: competency.questions?.length ?? 0,
                            })}
                        </Pills>
                    </div>
                </div>

                <div className={'cmp-card-actions flex flex-shrink-0 items-center'}>
                    {canAssignToEmployees && (
                        <button
                            type={'button'}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (isCompetencyInactive) {
                                    return;
                                }

                                onAssignToEmployees();
                            }}
                            className={
                                'text-navy focus-visible:ring-primary-800 rounded p-2 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-gray-600/70 disabled:hover:bg-transparent'
                            }
                            disabled={isCompetencyInactive}
                            title={
                                isCompetencyInactive
                                    ? t('competencies.actions.competencyDeactivatedTooltip')
                                    : t('competencies.assignToEmployees.cardAction')
                            }
                            data-testid={`competency-assign-employees-${competency.id}`}
                        >
                            <UserPlusIcon className={'h-5 w-5'} />
                        </button>
                    )}

                    <ActionMenu
                        items={actions}
                        dataTestId={`competency-actions-${competency.id}`}
                    />
                </div>
            </div>

            {isOpen && (
                <CompetencyQuestionsEditor
                    competencyId={competency.id}
                    competencyName={getTextByLanguage(competency.name, language)}
                    isOpen={isOpen}
                />
            )}
        </div>
    );
};
